import {
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

import { classList } from '@/utils/cn';

interface ScrollMaskProps {
  children: ReactNode;
  className?: string;
  initialFade?: boolean;
  showEndFade?: boolean;
  direction?: 'vertical' | 'horizontal';
  /** Length of the fade gradient in pixels. */
  fadeSize?: number;
  /** Length of the fade at the top or left, when it differs from `fadeSize` */
  startFadeSize?: number;
  /** Length of the fade at the bottom or right, when it differs from `fadeSize` */
  endFadeSize?: number;
  /** @deprecated Use showEndFade instead */
  showBottomFade?: boolean;
}

const VERTICAL_CLASSES = [
  'mask-linear',
  'mask-from-0',
  'mask-from-100',
  'mask-via-100',
  'mask-to-0',
  'mask-to-100',
  'mask-point-via-[40px]',
  'mask-point-via-[calc(100%-40px)]',
] as const;

const HORIZONTAL_CLASSES = [
  'mask-linear',
  'mask-dir-to-r',
  'mask-from-0',
  'mask-from-100',
  'mask-via-100',
  'mask-to-0',
  'mask-to-100',
  'mask-point-via-[40px]',
  'mask-point-via-[calc(100%-40px)]',
] as const;

export const ScrollMask = forwardRef<HTMLDivElement, ScrollMaskProps>(
  (
    {
      children,
      className,
      initialFade = false,
      showEndFade = false,
      direction = 'vertical',
      fadeSize = 40,
      startFadeSize = fadeSize,
      endFadeSize = fadeSize,
      showBottomFade,
    },
    ref
  ) => {
    const internalRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<number | null>(null);
    const lastMaskStateRef = useRef<{
      fadeEnd: boolean;
      fadeStart: boolean;
      gradient: string;
    } | null>(null);
    const effectiveShowEndFade = showEndFade || showBottomFade || false;
    const isHorizontal = direction === 'horizontal';

    const updateMaskClasses = useCallback(
      (element: HTMLDivElement) => {
        const hasScrolledFromStart = isHorizontal
          ? element.scrollLeft > 0
          : element.scrollTop > 0;
        const hasContentAfter = isHorizontal
          ? element.scrollLeft + element.clientWidth < element.scrollWidth - 1
          : element.scrollTop + element.clientHeight < element.scrollHeight - 1;

        const fadeStart = hasScrolledFromStart;
        const fadeEnd = effectiveShowEndFade && hasContentAfter;
        const dir = isHorizontal ? 'to right' : 'to bottom';
        const start = fadeStart
          ? `transparent, black ${startFadeSize}px`
          : 'black';
        const end = fadeEnd
          ? `black calc(100% - ${endFadeSize}px), transparent`
          : 'black';
        const gradient =
          !fadeStart && !fadeEnd
            ? ''
            : `linear-gradient(${dir}, ${start}, ${end})`;

        const lastMaskState = lastMaskStateRef.current;
        if (
          lastMaskState?.fadeStart === fadeStart &&
          lastMaskState.fadeEnd === fadeEnd &&
          lastMaskState.gradient === gradient
        ) {
          return;
        }

        lastMaskStateRef.current = { fadeStart, fadeEnd, gradient };

        const classesToRemove = isHorizontal
          ? HORIZONTAL_CLASSES
          : VERTICAL_CLASSES;
        element.classList.remove(...classesToRemove);
        element.style.maskImage = '';
        element.style.webkitMaskImage = '';

        if (!gradient) return;

        element.style.maskImage = gradient;
        element.style.webkitMaskImage = gradient;
      },
      [effectiveShowEndFade, isHorizontal, startFadeSize, endFadeSize]
    );

    const scheduleMaskUpdate = useCallback(
      (element: HTMLDivElement) => {
        if (frameRef.current !== null) return;
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = null;
          updateMaskClasses(element);
        });
      },
      [updateMaskClasses]
    );

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      scheduleMaskUpdate(e.currentTarget);
    };

    useEffect(() => {
      const element = internalRef.current;
      if (element) {
        scheduleMaskUpdate(element);
        return () => {
          if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
          frameRef.current = null;
        };
      }
    }, [children, scheduleMaskUpdate]);

    // the fade thresholds also move when the container resizes, not just
    // on scroll or children changes
    useLayoutEffect(() => {
      const element = internalRef.current;
      if (!element) return;
      const observer = new ResizeObserver(() => scheduleMaskUpdate(element));
      observer.observe(element);
      return () => observer.disconnect();
    }, [scheduleMaskUpdate]);

    const initialFadeClasses = initialFade
      ? isHorizontal
        ? 'mask-linear mask-dir-to-r mask-from-0 mask-via-100 mask-to-100 mask-point-via-[40px]'
        : 'mask-linear mask-from-0 mask-via-100 mask-to-100 mask-point-via-[40px]'
      : '';

    return (
      <div
        ref={(node) => {
          internalRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={classList(
          'overflow-scroll relative scrollbar-hover',
          initialFadeClasses,
          className
        )}
        onScroll={handleScroll}
      >
        {children}
      </div>
    );
  }
);

ScrollMask.displayName = 'ScrollMask';
